/**
 * useLegalDocument — geteiltes Fetch-/Draft-Muster für statisch ausgelieferte,
 * operator-verfasste Legal-Dokumente (`public/legal/*.md`).
 *
 * Spiegelt 1:1 die fail-closed Lade-Semantik aus `InboundActivation.tsx` (AVV-
 * Dokument): 404/leerer Body/SPA-HTML-Fallback ⇒ `missing`; Datei enthält den
 * Draft-Marker ⇒ `draft`; sonst ⇒ `ready`. Genutzt von {@link AdvisorConsentDialog}
 * (§3) und {@link KanzleiOnboardingDialog} (§5) sowie den einbettenden Panels, die
 * anhand von `ready` entscheiden, ob der neue Consent-/Onboarding-Schritt überhaupt
 * erzwungen wird (Inertheit: kein `ready` ⇒ Bestandsverhalten).
 *
 * @module components/tenant/useLegalDocument
 */

import { useEffect, useState } from 'react';
import logger from '../../utils/logger';

/** Ladezustand eines Legal-Dokuments (fail-closed default `loading`/`missing`). */
export type LegalDocState =
  | { kind: 'loading' }
  | { kind: 'ready'; text: string }
  | { kind: 'draft'; text: string }
  | { kind: 'missing' };

/**
 * Lädt ein statisches Legal-Dokument fail-closed. Jeder Fehler/404/Draft ⇒ NICHT
 * `ready`, sodass der aufrufende Flow inert bleibt bzw. der Dialog sperrt.
 *
 * @param path        öffentlicher Pfad des Dokuments (z. B. `/legal/…-v1.md`).
 * @param draftMarker Marker-String, dessen Vorkommen die Datei als Entwurf markiert.
 * @returns aktueller {@link LegalDocState}.
 */
export function useLegalDocument(path: string, draftMarker: string): LegalDocState {
  const [state, setState] = useState<LegalDocState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(path, { cache: 'no-cache' });
        if (!res.ok) {
          if (!cancelled) setState({ kind: 'missing' });
          return;
        }
        const text = await res.text();
        // Ein SPA liefert bei unbekannten Pfaden i. d. R. index.html (200) aus —
        // daher zusätzlich auf HTML/leer prüfen und als „fehlend" behandeln.
        const looksLikeHtml = /<!doctype html|<html/i.test(text.slice(0, 200));
        if (looksLikeHtml || text.trim() === '') {
          if (!cancelled) setState({ kind: 'missing' });
          return;
        }
        const isDraft = text.includes(draftMarker);
        if (!cancelled) {
          setState(isDraft ? { kind: 'draft', text } : { kind: 'ready', text });
        }
      } catch (err) {
        logger.error('useLegalDocument: fetch failed', err);
        if (!cancelled) setState({ kind: 'missing' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [path, draftMarker]);

  return state;
}

/**
 * Ersetzt einen Platzhalter (`{{name}}`) im Legal-Text durch einen Laufzeitwert.
 * Der Wortlaut selbst bleibt unangetastet — nur die eine Variable wird belegt
 * (§5.6 Wortlaut-Treue). Leerer/fehlender Wert ⇒ `fallback` (nie ein leeres
 * `{{ }}` im gerenderten Text).
 *
 * @param text        Roh-Text des Dokuments.
 * @param placeholder Platzhalter-Name OHNE Klammern (z. B. `kanzlei_name`).
 * @param value       Einzusetzender Wert (getrimmt).
 * @param fallback    Ersatz, wenn `value` leer/null ist.
 */
export function interpolatePlaceholder(
  text: string,
  placeholder: string,
  value: string | null | undefined,
  fallback: string,
): string {
  const resolved = typeof value === 'string' && value.trim().length > 0 ? value.trim() : fallback;
  // Platzhalter-Name ist eine feste Konstante (kein User-Input) ⇒ Regex-sicher.
  const pattern = new RegExp(`\\{\\{\\s*${placeholder}\\s*\\}\\}`, 'g');
  return text.replace(pattern, resolved);
}
